import React, { useEffect, useState, useRef } from 'react';
import {
  Box, Card, Table, TableHead, TableRow, TableCell, TableBody,
  Typography, TextField, InputAdornment, Chip, IconButton, Tooltip,
  useMediaQuery, useTheme, MenuItem, Button,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, LinearProgress, Stack, Divider
} from '@mui/material';
import {
  Search, Visibility, ArrowUpward, ArrowDownward,
  UploadFile, PictureAsPdf, Close, Add
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import PageHeader from '../common/PageHeader';
import { MobileCard, MobileRow } from '../common/MobileCard';

// ── tiny file-pick chip ───────────────────────────────────────────────────────
function FilePick({ label, accept, file, onChange, required, color = 'primary' }) {
  const ref = useRef();
  return (
    <Box>
      <input ref={ref} type="file" accept={accept} style={{ display: 'none' }}
        onChange={e => onChange(e.target.files[0] || null)} />
      <Chip
        icon={<PictureAsPdf />}
        label={file ? file.name : `${label}${required ? ' *' : ''}`}
        color={file ? color : 'default'}
        variant={file ? 'filled' : 'outlined'}
        onClick={() => ref.current.click()}
        onDelete={file ? () => { onChange(null); ref.current.value = ''; } : undefined}
        deleteIcon={<Close />}
        sx={{ cursor: 'pointer', maxWidth: 260, fontSize: 12 }}
      />
      {required && !file && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.3, ml: 0.5 }}>
          Required
        </Typography>
      )}
    </Box>
  );
}

// ── Upload Dialog ─────────────────────────────────────────────────────────────
function UploadBOEDialog({ open, onClose, onSuccess }) {
  const [mode, setMode]       = useState('pdf'); // 'pdf' | 'zip'
  const [boePdf, setBoePdf]   = useState(null);
  const [awbPdf, setAwbPdf]   = useState(null);
  const [ciPdf,  setCiPdf]    = useState(null);
  const [rodPdf, setRodPdf]   = useState(null);
  const [zipFile, setZipFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const zipRef = useRef();

  const reset = () => {
    setBoePdf(null); setAwbPdf(null); setCiPdf(null); setRodPdf(null); setZipFile(null);
    setError(''); setLoading(false); setMode('pdf');
  };

  const handleClose = () => { reset(); onClose(); };

  const handleSubmit = async () => {
    setError('');
    if (mode === 'pdf' && !boePdf) { setError('BOE / Entry PDF is required.'); return; }
    if (mode === 'zip' && !zipFile) { setError('Please select a ZIP file.'); return; }

    const fd = new FormData();
    if (mode === 'pdf') {
      fd.append('boe_pdf', boePdf);
      if (awbPdf)  fd.append('awb_pdf', awbPdf);
      if (ciPdf)   fd.append('ci_pdf',  ciPdf);
      if (rodPdf)  fd.append('rod_pdf', rodPdf);
    } else {
      fd.append('zip_file', zipFile);
    }

    setLoading(true);
    try {
      const res = await api.post('/historical-boe/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      reset();
      onClose();
      onSuccess(res.data.data);
    } catch (e) {
      setError(e.response?.data?.message || 'Upload failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <UploadFile color="primary" />
          Add New BOE Entry
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {/* Mode toggle */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2.5 }}>
          <Chip label="Upload PDFs" color={mode === 'pdf' ? 'primary' : 'default'}
            variant={mode === 'pdf' ? 'filled' : 'outlined'}
            onClick={() => { setMode('pdf'); setZipFile(null); setError(''); }}
            sx={{ cursor: 'pointer' }} />
          <Chip label="Upload ZIP" color={mode === 'zip' ? 'primary' : 'default'}
            variant={mode === 'zip' ? 'filled' : 'outlined'}
            onClick={() => { setMode('zip'); setBoePdf(null); setAwbPdf(null); setCiPdf(null); setRodPdf(null); setError(''); }}
            sx={{ cursor: 'pointer' }} />
        </Box>

        {mode === 'pdf' ? (
          <Stack spacing={1.5}>
            <Typography variant="caption" color="text.secondary">
              Upload individual PDF files. The BOE/Entry PDF is required; others are optional.
            </Typography>
            <FilePick label="BOE / Entry PDF" accept=".pdf" file={boePdf} onChange={setBoePdf} required color="primary" />
            <Divider />
            <FilePick label="AWB PDF"               accept=".pdf" file={awbPdf} onChange={setAwbPdf} color="default" />
            <FilePick label="Commercial Invoice PDF" accept=".pdf" file={ciPdf}  onChange={setCiPdf}  color="success" />
            <FilePick label="FedEx ROD PDF"          accept=".pdf" file={rodPdf} onChange={setRodPdf} color="warning" />
          </Stack>
        ) : (
          <Stack spacing={1.5}>
            <Typography variant="caption" color="text.secondary">
              Upload a ZIP containing all documents. Files will be classified automatically by filename.
            </Typography>
            <Box>
              <input ref={zipRef} type="file" accept=".zip" style={{ display: 'none' }}
                onChange={e => setZipFile(e.target.files[0] || null)} />
              <Chip
                icon={<UploadFile />}
                label={zipFile ? zipFile.name : 'Select ZIP file *'}
                color={zipFile ? 'primary' : 'default'}
                variant={zipFile ? 'filled' : 'outlined'}
                onClick={() => zipRef.current.click()}
                onDelete={zipFile ? () => { setZipFile(null); zipRef.current.value = ''; } : undefined}
                deleteIcon={<Close />}
                sx={{ cursor: 'pointer', maxWidth: 300, fontSize: 12 }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              Files containing "ENTRY" or "BOE" in the name → BOE · "AWB" → AWB · "CI" or "INVOICE" → Commercial Invoice · "ROD" → ROD
            </Typography>
          </Stack>
        )}

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading} startIcon={<UploadFile />}>
          {loading ? 'Uploading…' : 'Upload & Parse'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main List ─────────────────────────────────────────────────────────────────
export default function HistoricalBOEList() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const [list, setList]         = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch]     = useState('');
  const [yearFilter, setYearFilter] = useState('all');
  const [dateSort, setDateSort] = useState('desc');
  const [uploadOpen, setUploadOpen] = useState(false);

  useEffect(() => {
    api.get('/historical-boe').then(r => setList(r.data.data || []));
  }, []);

  // Derive year options from data
  const years = [...new Set(list.map(b => b.ooc_date?.substring(0, 4)).filter(Boolean))].sort().reverse();

  useEffect(() => {
    let d = list;
    if (search) {
      const q = search.toLowerCase();
      d = d.filter(b =>
        b.cbe_number?.toLowerCase().includes(q) ||
        b.hawb_number?.toLowerCase().includes(q) ||
        b.consignor_name?.toLowerCase().includes(q) ||
        b.invoice_number?.toLowerCase().includes(q) ||
        b.description_of_goods?.toLowerCase().includes(q) ||
        String(b.folder_ref || '').includes(q)
      );
    }
    if (yearFilter !== 'all') d = d.filter(b => b.ooc_date?.startsWith(yearFilter));
    d = [...d].sort((a, b) => {
      const diff = new Date(a.ooc_date || 0) - new Date(b.ooc_date || 0);
      return dateSort === 'asc' ? diff : -diff;
    });
    setFiltered(d);
  }, [search, yearFilter, dateSort, list]);

  const fmt = (v, d = 2) => v != null ? parseFloat(v).toLocaleString('en-IN', { minimumFractionDigits: d, maximumFractionDigits: d }) : '—';

  const handleUploadSuccess = (newRec) => {
    setList(prev => [newRec, ...prev]);
    navigate(`/historical-boe/${newRec.id}`);
  };

  return (
    <Box>
      <PageHeader
        title="Historical BOE"
        subtitle={`${list.length} bill${list.length !== 1 ? 's' : ''} of entry — imported records`}
        action={
          <Button variant="contained" startIcon={<Add />} onClick={() => setUploadOpen(true)}>
            Add New BOE
          </Button>
        }
      />

      <UploadBOEDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onSuccess={handleUploadSuccess}
      />

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <TextField size="small" placeholder="Search CBE, HAWB, supplier, invoice…"
          value={search} onChange={e => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 200 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
        />
        <TextField size="small" select value={yearFilter} onChange={e => setYearFilter(e.target.value)} label="Year" sx={{ minWidth: 110 }}>
          <MenuItem value="all">All Years</MenuItem>
          {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
        </TextField>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        {filtered.length} record{filtered.length !== 1 ? 's' : ''}
        {search || yearFilter !== 'all' ? ` (filtered from ${list.length})` : ''}
      </Typography>

      {isMobile ? (
        <Box>
          {filtered.map(b => (
            <MobileCard key={b.id}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box>
                  <Typography variant="body2" fontWeight={700} sx={{ fontFamily: 'monospace', fontSize: 11, color: 'primary.main' }}>
                    {b.cbe_number || `#${b.folder_ref}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{b.ooc_date || '—'}</Typography>
                </Box>
                <IconButton size="small" onClick={() => navigate(`/historical-boe/${b.id}`)} sx={{ color: 'primary.main' }}>
                  <Visibility fontSize="small" />
                </IconButton>
              </Box>
              <MobileRow label="Supplier"  value={b.consignor_name} />
              <MobileRow label="HAWB"      value={b.hawb_number} mono />
              <MobileRow label="Invoice"   value={b.invoice_number ? `${b.invoice_number} · USD ${fmt(b.invoice_value)}` : '—'} />
              <MobileRow label="Assess. Value" value={b.assessable_value ? `₹ ${fmt(b.assessable_value, 0)}` : '—'} bold />
            </MobileCard>
          ))}
          {filtered.length === 0 && (
            <Card><Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>No records found</Box></Card>
          )}
        </Box>
      ) : (
        <Card>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40, color: 'text.secondary', fontSize: 11 }}>#</TableCell>
                <TableCell>CBE Number</TableCell>
                <TableCell
                  onClick={() => setDateSort(s => s === 'asc' ? 'desc' : 'asc')}
                  sx={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                >
                  OOC Date {dateSort === 'asc'
                    ? <ArrowUpward sx={{ fontSize: 12, verticalAlign: 'middle' }} />
                    : <ArrowDownward sx={{ fontSize: 12, verticalAlign: 'middle' }} />}
                </TableCell>
                <TableCell>Supplier</TableCell>
                <TableCell>HAWB</TableCell>
                <TableCell>Invoice</TableCell>
                <TableCell align="right">USD Value</TableCell>
                <TableCell align="right">Assess. Value (₹)</TableCell>
                <TableCell align="right">Duty (₹)</TableCell>
                <TableCell align="center">Docs</TableCell>
                <TableCell align="center">View</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                    No records found
                  </TableCell>
                </TableRow>
              ) : filtered.map(b => (
                <TableRow key={b.id} hover>
                  <TableCell sx={{ color: 'text.secondary', fontSize: 11 }}>{b.folder_ref}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace', fontSize: 11, color: 'primary.main' }}>
                      {b.cbe_number || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>{b.ooc_date || '—'}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontSize={12} fontWeight={500}>{b.consignor_name?.split(' ').slice(0, 4).join(' ') || '—'}</Typography>
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{b.hawb_number || '—'}</TableCell>
                  <TableCell sx={{ fontSize: 11 }}>{b.invoice_number || '—'}</TableCell>
                  <TableCell align="right" sx={{ fontSize: 12, fontWeight: 600, color: 'primary.main' }}>
                    {b.invoice_value ? `$${fmt(b.invoice_value)}` : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 12 }}>
                    {b.assessable_value ? `₹${fmt(b.assessable_value, 0)}` : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 12, color: '#e65100' }}>
                    {b.duty_amount ? `₹${fmt(b.duty_amount, 0)}` : '—'}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.3, justifyContent: 'center', flexWrap: 'wrap' }}>
                      {b.boe_path     && <Chip label="BOE" size="small" color="primary"  variant="outlined" sx={{ fontSize: 9, height: 18 }} />}
                      {b.awb_path     && <Chip label="AWB" size="small" color="default"  variant="outlined" sx={{ fontSize: 9, height: 18 }} />}
                      {b.invoice_path && <Chip label="CI"  size="small" color="success"  variant="outlined" sx={{ fontSize: 9, height: 18 }} />}
                      {b.rod_path     && <Chip label="ROD" size="small" color="warning"  variant="outlined" sx={{ fontSize: 9, height: 18 }} />}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="View Details">
                      <IconButton size="small" onClick={() => navigate(`/historical-boe/${b.id}`)} sx={{ color: 'primary.main' }}>
                        <Visibility fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </Box>
  );
}
